import React from 'react';

// Components
import TableMenu, { MenuItem } from '@/views/components/data/DataTableV2/TableMenu';
import { Checkbox } from '@/views/components/inputs';
import { IconButton } from '@/views/components/buttons';

// Libraries
import PerfectScrollbar from 'react-perfect-scrollbar';
import { CellContext, ColumnDef, ColumnSort, Row, Table } from '@tanstack/react-table';
import { isObject } from 'lodash';

// Utils
import { formatCurrency, formatLocalizedString, formatPercentage } from '@/utils/i18n';
import { formatMessage } from './i18n';

// Types
import { GeneratedColumnDefParams, GetSelectableColumnDefParams } from '@/types/data-table';
import { SortingField, SortOrder } from '@/types/data';
import { withTooltipWrapper } from '@/views/components/tooltips/withTooltipWrapper';

export const getSelectableHeader = <T extends object>(
  table: Table<T>,
  isDisabled?: (row: Row<T>) => boolean,
  isForcefullySelected?: (row: Row<T>) => boolean,
) => {
  const allRows = table.getPreFilteredRowModel().rows;
  const selectableRows = allRows.filter(row => !isDisabled?.(row) && !isForcefullySelected?.(row));
  const allSelected = selectableRows.every(row => row.getIsSelected());

  const handleChange = () => {
    selectableRows.forEach(row => row.toggleSelected(!allSelected));
  };

  return (
    <Checkbox
      ariaLabel={formatMessage(allSelected ? 'Common.Accessibility.DeselectAllRows' : 'Common.Accessibility.SelectAllRows')}
      onChange={handleChange}
      selected={selectableRows.length > 0 && allSelected}
      stopClickPropagation
      disabled={selectableRows.length === 0}
    />
  );
};
/**
 * Generates a column definition for a selectable column in a data table.
 *
 * @template T - The type of the data objects in the table.
 * @param {boolean} [enableMultiselect=false] - Flag to enable or disable multi-select functionality.
 * @param {function(Row<T>): boolean} isDisabled - Function to determine if a row should be disabled for selection.
 * @returns {ColumnDef<T>} - The column definition object for the selectable column.
 *
 * The returned column definition includes:
 * - An optional header component with a checkbox for selecting/deselecting all rows (if multi-select is enabled).
 * - A cell component with a checkbox for selecting/deselecting individual rows.
 */
export const getSelectableColumnDef = <T extends object>(
  options: GetSelectableColumnDefParams<T> = {
    enableMultiselect: false,
    rowSelectionDisabled: (row: Row<T>) => false,
    rowForcefullySelected: (row: Row<T>) => false,
    getCheckboxTooltip: (row: Row<T>) => ({ message: '', title: '' }),
  },
): ColumnDef<T> => {
  const { enableMultiselect, rowSelectionDisabled, rowForcefullySelected, getCheckboxTooltip, getRowAriaLabelledBy } = options;
  const header = enableMultiselect ? ({ table }: { table: Table<T> }) => getSelectableHeader(table, rowSelectionDisabled, rowForcefullySelected) : undefined;

  return {
    id: 'select',
    header,
    size: 44,
    cell: ({ row }: { row: Row<T> }) => {
      const isForcefullySelected = rowForcefullySelected?.(row) ?? false;
      const selectionIsDisabled = rowSelectionDisabled?.(row) ?? false;
      const isDisabled = selectionIsDisabled || isForcefullySelected || !row.getCanSelect();
      const isSelected = row.getIsSelected() || isForcefullySelected;
      const ariaLabelledBy = getRowAriaLabelledBy?.(row);

      return withTooltipWrapper(
        <Checkbox ariaLabelledby={ariaLabelledBy} disabled={isDisabled} onChange={row.getToggleSelectedHandler()} selected={isSelected} stopClickPropagation />,
        getCheckboxTooltip?.(row) ?? { message: '' },
      );
    },
  };
};

/**
 * Generates a column definition for a table menu.
 *
 * @template T - The type of the row data.
 * @param {MenuItem<T>[]} menuItems - An array of menu items to be used in the table menu.
 * @returns {ColumnDef<T>} The column definition for the table menu.
 */
export function getTableMenuColumnDef<T extends object>(menuItems: MenuItem<T>[], psbRef: React.MutableRefObject<PerfectScrollbar | null>): ColumnDef<T> {
  return {
    accessorKey: 'TableMenu',
    cell: (info: CellContext<T, unknown>) => {
      const preparedMenuItems = prepareMenuItems(menuItems, info.row.original);
      return <TableMenu menuItems={preparedMenuItems} psbRef={psbRef} />;
    },
    enableSorting: false,
    header: '',
    size: 48,
  };
}

/**
 * Prepares a list of menu items by filtering and mapping them based on the provided row data.
 *
 * Exported for unit tests only
 *
 * @template T - The type of the row data.
 * @param {MenuItem<T>[]} menuItems - The list of menu items to be prepared.
 * @param {T} rowData - The data of the row to be used for filtering and mapping the menu items.
 * @returns {MenuItem<T>[]} - The filtered and mapped list of menu items.
 */
export function prepareMenuItems<T extends object>(menuItems: MenuItem<T>[], rowData: T): MenuItem<T>[] {
  return menuItems
    .filter(item => item.isAllowed(rowData))
    .map(item => ({
      ...item,
      onClick: () => item.onClick(rowData),
    }));
}

/**
 * Generates the column definition for an expandable row.
 *
 * @template T - The type of the row data.
 * @param {function(Row<T>): JSX.Element} renderExpandedContent - A function that renders the expanded content for a row.
 * @returns {ColumnDef<T>} The column definition for the expander.
 */
export const getExpandedColumnDef = <T extends object>(renderExpandedContent: (row: Row<T>) => JSX.Element, tableUniqueId: string): ColumnDef<T> => ({
  id: 'expander',
  header: '',
  size: 44,
  cell: ({ row }: { row: Row<T> }) => {
    const isExpanded = row.getIsExpanded();
    const canExpand = row.getCanExpand();
    const handleRowExpand = () => {
      if (renderExpandedContent && !isExpanded) {
        renderExpandedContent(row);
      }
      row.toggleExpanded();
    };

    const iconClass = isExpanded ? 'row-collapse' : 'row-expand';
    const ariaLabel = isExpanded ? formatMessage('Common.Accessibility.CollapseRow') : formatMessage('Common.Accessibility.ExpandRow');
    const ariaExpanded = !!isExpanded;
    const ariaControls = `expanded-content-${row.index}-${tableUniqueId}`;
    return (
      <IconButton
        ariaLabel={ariaLabel}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        disabled={!canExpand}
        className={`expand-icon ${iconClass}`}
        icon="arrow-drop-down"
        onClick={handleRowExpand}
      />
    );
  },
});

export const getSubRowColumnDef = <T extends object>(tableUniqueId: string): ColumnDef<T> => ({
  id: 'expander',
  header: '',
  size: 44,
  cell: ({ row }: { row: Row<T> }) => {
    const isTopLevel = row.depth === 0;
    const hasSubRows = Array.isArray(row.subRows) && row.subRows.length > 0;
    const canExpand = row.getCanExpand();

    if (!isTopLevel || !hasSubRows || !canExpand) {
      return <span />;
    }

    const isExpanded = row.getIsExpanded();
    const iconClass = isExpanded ? 'row-collapse' : 'row-expand';
    const ariaLabel = formatMessage(isExpanded ? 'Common.Accessibility.CollapseRow' : 'Common.Accessibility.ExpandRow');
    const ariaControls = `expanded-content-${row.index}-${tableUniqueId}`;

    const handleRowExpand = (e: React.MouseEvent) => {
      row.toggleExpanded();
    };

    return (
      <IconButton
        ariaLabel={ariaLabel}
        aria-expanded={isExpanded}
        aria-controls={ariaControls}
        disabled={!canExpand}
        className={`expand-icon ${iconClass}`}
        icon="arrow-drop-down"
        onClick={handleRowExpand}
      />
    );
  },
});

/**
 * Generates the column definitions for the DataTableV2 based on the provided parameters.
 *
 * @template T - The type of the row data.
 * @param {Object} params - The parameters for generating column definitions.
 * @param {ColumnDef<T>[]} params.columns - The base columns to include in the column definitions.
 * @param {boolean} [params.enableTableSelection=false] - Whether to enable table selection.
 * @param {boolean} [params.enableMultiselect=false] - Whether to enable multi-selection.
 * @param {boolean} [params.isExpandable=false] - Whether the rows are expandable.
 * @param {Array} [params.menuItems] - The menu items to include in the table menu column.
 * @param {function(Row<T>): JSX.Element} [params.renderExpandedContent] - A function that renders the expanded content for a row.
 * @param {function(Row<T>): boolean} [params.rowSelectionDisabled] - Function to determine if a row should be disabled for selection.
 * @returns {ColumnDef<T>[]} The array of column definitions.
 */
export const generateColumnDef = <T extends object>({
  tableUniqueId,
  columns,
  enableTableSelection = false,
  enableMultiselect = false,
  isExpandable = false,
  menuItems,
  psbRef,
  renderExpandedContent,
  rowSelectionDisabled = (row: Row<T>) => false,
  rowForcefullySelected = (row: Row<T>) => false,
  getCheckboxTooltip = (row: Row<T>) => ({ message: '', title: '' }),
  getRowAriaLabelledBy,
  getSubRows,
}: GeneratedColumnDefParams<T>): ColumnDef<T>[] => {
  const columnDefs: ColumnDef<T>[] = [];

  if (isExpandable) {
    if (getSubRows) {
      columnDefs.push(getSubRowColumnDef<T>(tableUniqueId));
    } else if (renderExpandedContent) {
      columnDefs.push(getExpandedColumnDef<T>(renderExpandedContent, tableUniqueId));
    }
  }

  if (enableTableSelection) {
    columnDefs.push(
      getSelectableColumnDef<T>({
        enableMultiselect,
        rowSelectionDisabled,
        rowForcefullySelected,
        getCheckboxTooltip,
        getRowAriaLabelledBy,
      }),
    );
  }

  columnDefs.push(...columns);

  if (menuItems?.length) {
    const menuColumn = getTableMenuColumnDef(menuItems, psbRef);
    columnDefs.push(menuColumn);
  }

  return columnDefs;
};

/**
 * Generates a JSX element for a data table header with a formatted message.
 *
 * Used by getCustomColumnDef
 *
 * @param i18nBasePath - The base path for the internationalization key.
 * @param i18nKey - The specific key for the header text.
 * @returns A JSX element containing the formatted header text.
 */
const getHeader = (i18nBasePath: string, i18nKey: string): JSX.Element => <span>{formatMessage(`${i18nBasePath}.Headers.${i18nKey}`, undefined, '')}</span>;

/**
 * Generates a column definition for a data table with a custom cell renderer.
 *
 * @template T - The type of the data object.
 * @param accessor - The key to access the value in the data object.
 * @param i18nBasePath - The base path for internationalization.
 * @param renderValue - The function to render the cell value.
 * @param columnDef - Optional custom column definition to extend.
 * @returns The generated column definition.
 */
export const getCustomColumnDef = <T extends object>(
  accessor: string,
  i18nBasePath: string,
  renderValue: (cell: CellContext<T, any>, options?: any) => React.JSX.Element,
  columnDef: ColumnDef<T> = {} as ColumnDef<T>,
): ColumnDef<T> => {
  const defaultColumnDef = {
    id: accessor,
    accessorKey: accessor,
    enableSorting: true,
    header: () => getHeader(i18nBasePath, accessor),
  };

  const mergedColumnDef = { ...defaultColumnDef, ...columnDef };

  return {
    ...mergedColumnDef,
    cell: renderValue,
  };
};

/**
 * Generates a column definition for a data table with a custom value renderer.
 *
 * @template T - The type of the data object.
 * @param {string} accessor - The key to access the value in the data object.
 * @param {string} i18nBasePath - The base path for internationalization.
 * @param {ColumnDef<T>} [columnDef] - Optional custom column definition to extend.
 * @param {(value: string) => React.JSX.Element} [valueTransformation] - Optional function to transform the cell value.
 * @param {any} [options] - Additional options for the value transformation.
 * @returns {ColumnDef<T>} - The generated column definition.
 */
export const getValueColumnDef = <T extends object>(
  accessor: string,
  i18nBasePath: string,
  columnDef?: ColumnDef<T>,
  valueTransformation?: (value: string) => React.JSX.Element,
  options?: any,
): ColumnDef<T> => getCustomColumnDef(accessor, i18nBasePath, getDefaultRenderValueForCell(valueTransformation, options), columnDef);

/**
 * Generates a default render function for a cell in a data table.
 *
 * Format of generated element is :
 * <span data-private={dataPrivate} data-dd-privacy={dataDdPrivacy}>{valueTransformation ? valueTransformation(cell.getValue()) : cell.getValue()}</span>
 *
 * @param valueTransformation - An optional function to transform the cell value before rendering.
 * @param options - Optional configuration for rendering.
 * @param options.dataPrivate - Indicates if the data is private. Defaults to `true`.
 * @param options.dataDdPrivacy - Indicates if the data is private. Defaults to `mask`.
 * @returns A function that takes a `CellContext` and returns a JSX element for rendering the cell.
 */
const getDefaultRenderValueForCell =
  (
    valueTransformation?: (value: string) => React.JSX.Element,
    options?: {
      dataPrivate?: boolean;
      dataDdPrivacy?: 'allow' | 'mask' | 'hidden' | 'mask-user-input';
    },
  ) =>
  (cell: CellContext<any, any>) => {
    const { dataPrivate = true, dataDdPrivacy = 'mask' } = options || {};

    const spanAttributes: {
      'data-private'?: boolean;
      'data-dd-privacy'?: 'allow' | 'mask' | 'hidden' | 'mask-user-input';
    } = {};

    if (dataPrivate) {
      spanAttributes['data-private'] = true;
    }

    if (dataDdPrivacy) {
      spanAttributes['data-dd-privacy'] = dataDdPrivacy;
    }

    return <span {...spanAttributes}>{valueTransformation ? valueTransformation(cell.getValue()) : cell.getValue()}</span>;
  };

export const renderValueCell = <T extends object>(cell: CellContext<T, any>) => (
  <span data-private data-dd-privacy="mask">
    {cell.getValue()}
  </span>
);

export const renderCurrencyCell = <T extends object>(cell: CellContext<T, any>) => {
  const value = formatCurrency(cell.getValue());
  return (
    <span data-private data-dd-privacy="mask">
      {value}
    </span>
  );
};

export const renderPercentageCell = <T extends object>(cell: CellContext<T, any>) => {
  const value = formatPercentage(cell.getValue());
  return (
    <span data-private data-dd-privacy="mask">
      {value}
    </span>
  );
};

const customSorting = <T extends object>(rowA: Row<T>, rowB: Row<T>, columnId: string) => {
  const rawRowValueA: string | number = rowA.getValue(columnId);
  const rawRowValueB: string | number = rowB.getValue(columnId);

  // Default sorting logic for the remaining rows
  const valueA: string | number = isObject(rawRowValueA) ? formatLocalizedString(rawRowValueA).toLocaleLowerCase() : rawRowValueA;
  const valueB: string | number = isObject(rawRowValueB) ? formatLocalizedString(rawRowValueB).toLocaleLowerCase() : rawRowValueB;

  if (valueA > valueB) {
    return 1;
  }
  if (valueA < valueB) {
    return -1;
  }

  return 0;
};

/**
 * Custom sorting function that sorts rows based on an order or the given `orderArray`.
 *
 * @template T - The type of the row data.
 * @param {Row<T>} rowA - The first row to compare.
 * @param {Row<T>} rowB - The second row to compare.
 * @param {string} columnId - The ID of the column being sorted.
 * @param {T[]} orderArray - The array of values to use for custom sorting.
 * @returns {number} - Returns 0 if either row is the first row, otherwise returns the result of the custom sorting function.
 */
export const customOrderSorting =
  <T extends Object>(orderArray: (string | number)[]) =>
  (rowA: Row<T>, rowB: Row<T>, columnId: string) => {
    const rawRowValueA: string | number = rowA.getValue(columnId);
    const rawRowValueB: string | number = rowB.getValue(columnId);

    const valueA: string | number = isObject(rawRowValueA) ? formatLocalizedString(rawRowValueA) : rawRowValueA;
    const valueB: string | number = isObject(rawRowValueB) ? formatLocalizedString(rawRowValueB) : rawRowValueB;

    const indexA = orderArray.indexOf(valueA);
    const indexB = orderArray.indexOf(valueB);

    return indexA - indexB;
  };

/**
 * Custom sorting function that skips the first row (index 0) when sorting.
 *
 * Used mostly to skip the total row in the metrics table of the new portfolio editor.
 *
 * @template T - The type of the row data.
 * @param {Row<T>} rowA - The first row to compare.
 * @param {Row<T>} rowB - The second row to compare.
 * @param {string} columnId - The ID of the column being sorted.
 * @returns {number} - Returns 0 if either row is the first row, otherwise returns the result of the custom sorting function.
 */
const skipFirstCustomSorting = <T extends object>(rowA: Row<T>, rowB: Row<T>, columnId: string) => {
  if (rowA.index === 0 || rowB.index === 0) {
    return 0;
  }

  return customSorting(rowA, rowB, columnId);
};

export { customSorting, skipFirstCustomSorting };

/**
 * Updates the sorting state of a table based on the provided sorting field data and sort column.
 *
 * @param sortingFieldData - An array of current sorting fields, where order is a string of either asc or desc
 * @param columnSort - The column to be sorted, containing the column id and sort direction, where desc is a boolean
 * @returns An object containing the new sorting state and updated sort column.
 *
 * @example
 * const sortingFieldData = [{ id: 'name', order: SortOrder.Asc }];
 * const columnSort = { id: 'age', desc: true };
 */
export const getTableSortedColumns = (
  sortingFieldData: SortingField[],
  columnSort: ColumnSort,
): { serverSortingParams: SortingField[]; columnSort: ColumnSort[] } => {
  const { id, desc } = columnSort;
  const selectedSortingData = sortingFieldData.find((s: SortingField) => s.id === id);

  let order = SortOrder.Asc;

  if (!selectedSortingData) {
    order = SortOrder[desc ? 'Desc' : 'Asc'];
  } else {
    order = selectedSortingData.order === SortOrder.Desc ? SortOrder.Asc : SortOrder.Desc;
  }

  return {
    serverSortingParams: [{ id, order }],
    columnSort: [{ id, desc: order === SortOrder.Desc }],
  };
};
